import React, { useState } from "react";
import { Plus, Edit2, Trash2, X } from "lucide-react";
import { useAdmin } from "../admin-context";
import DataTable from "../DataTable";

const Categories = () => {
  const { categories, addCategory, updateCategory, deleteCategory, hasPermission } = useAdmin();
  const canCreate = hasPermission("categories.create");
  const canEdit   = hasPermission("categories.edit");
  const canDelete = hasPermission("categories.delete");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  // Form State
  const [formData, setFormData] = useState({
    name: "",
    description: ""
  });

  const handleOpen = (category = null) => {
    if (category) {
      setEditingId(category.id);
      setFormData({
        name: category.name,
        description: category.description || "",
        license_id: 2
      });
    } else {
      setEditingId(null);
      setFormData({ name: "", description: "", license_id: 2 });
    }
    setIsModalOpen(true);
  };

  // Simulate Image Upload
  // const handleImageUpload = (e) => {
  //   const file = e.target.files[0];
  //   if (file) {
  //     const fakeUrl = URL.createObjectURL(file);
  //     setFormData({ ...formData, image: fakeUrl });
  //   }
  // };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingId) {
      updateCategory(editingId, formData);
    } else {
      addCategory(formData);
    }
    setIsModalOpen(false);
  };

  const columns = [
    {
      key: "name",
      label: "Name",
      sortable: true,
      render: (val) => (
        <span className="font-bold text-gray-900 capitalize">{val}</span>
      ),
    },
    {
      key: "description",
      label: "Description",
      render: (val) => (
        <p className="text-sm text-gray-500 truncate max-w-xs">{val || "No description provided."}</p>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Categories</h1>

      <DataTable
        columns={columns}
        data={categories}
        searchKeys={["name", "description"]}
        searchPlaceholder="Search categories..."
        defaultPageSize={10}
        pageSizeOptions={[10, 25, 50]}
        emptyText="No categories found."
        headerActions={
          canCreate && (
            <button
              onClick={() => handleOpen()}
              className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-xl flex items-center gap-2 font-bold shadow-lg shadow-orange-200 transition active:scale-95 text-sm"
            >
              <Plus size={16} /> Add Category
            </button>
          )
        }
        actions={(row) => (
          <>
            {canEdit && (
              <button
                onClick={() => handleOpen(row)}
                className="p-2 hover:bg-orange-50 rounded-lg text-orange-500 transition"
                title="Edit"
              >
                <Edit2 size={15} />
              </button>
            )}
            {canDelete && (
              <button
                onClick={() => deleteCategory(row.id)}
                className="p-2 hover:bg-red-50 rounded-lg text-red-400 transition"
                title="Delete"
              >
                <Trash2 size={15} />
              </button>
            )}
          </>
        )}
      />

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="flex justify-between items-center p-5 border-b bg-gray-50">
              <h3 className="text-xl font-bold text-gray-800">{editingId ? "Edit Category" : "Add Category"}</h3>
              <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-600 hover:bg-gray-200 p-1 rounded-full transition"><X size={20} /></button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              {/* Image Uploader */}
              {/* <div className="flex flex-col items-center justify-center w-full">
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-xl cursor-pointer bg-gray-50 hover:bg-gray-100 transition">
                    {formData.image ? (
                        <img src={formData.image} alt="Preview" className="h-full object-contain p-2 rounded-lg" />
                    ) : (
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                            <UploadCloud className="w-8 h-8 mb-2 text-gray-400" />
                            <p className="text-xs text-gray-500 font-medium">Click to upload icon</p>
                        </div>
                    )}
                    <input type="file" className="hidden" onChange={handleImageUpload} accept="image/*" />
                </label>
              </div> */}

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Category Name</label>
                <input 
                    required 
                    value={formData.name} 
                    onChange={(e) => setFormData({...formData, name: e.target.value})} 
                    className="w-full border border-gray-300 px-3 py-2.5 rounded-lg outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition" 
                    placeholder="e.g. Electronics"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Description</label>
                <textarea 
                    required 
                    rows={3} 
                    value={formData.description} 
                    onChange={(e) => setFormData({...formData, description: e.target.value})} 
                    className="w-full border border-gray-300 px-3 py-2.5 rounded-lg outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none transition" 
                    placeholder="Short description..."
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button 
                    type="button" 
                    onClick={() => setIsModalOpen(false)} 
                    className="flex-1 py-2.5 border border-gray-300 rounded-lg font-bold text-gray-700 hover:bg-gray-50 transition"
                >
                    Cancel
                </button>
                <button 
                    type="submit" 
                    className="flex-1 py-2.5 bg-orange-500 text-white rounded-lg font-bold hover:bg-orange-600 shadow-md transition"
                >
                    {editingId ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Categories;